import React, { useEffect, useState } from 'react';
import { Globe, Languages, Type, Check, X, Zap } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from './ui/dialog';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { useLiteMode } from '../context/LiteModeContext';
import { CountryPicker } from './zakat/CountryPicker';

/**
 * SettingsSheet — single accessible dialog that consolidates the three
 * top-nav controls (country · language · text size) that previously lived
 * separately in the header. Opens from the "EN" settings chip in
 * SiteHeader.
 *
 * • Reuses the existing CountryPicker so the country + currency + Nisab
 *   basis machinery keeps working.
 * • Reuses the existing `languages` array from ZakatGlobalContext.
 * • Uses the existing localStorage-backed text-size mechanism
 *   (`zi_text_size` key) so preference persists across routes.
 *
 * Accessibility:
 *   • Radix `Dialog` handles focus trap, ESC to close, aria-modal,
 *     and returns focus to the trigger.
 *   • Each control has an explicit label + aria-pressed/aria-selected.
 */
const TEXT_SIZE_LEVELS = [
  { key: 'A',   size: '100%',   label: 'Standard'     },
  { key: 'A+',  size: '112.5%', label: 'Large'        },
  { key: 'A++', size: '125%',   label: 'Extra large'  },
];
const TEXT_SIZE_KEY = 'zi_text_size';

const readTextSize = () => {
  try {
    const v = localStorage.getItem(TEXT_SIZE_KEY);
    const idx = TEXT_SIZE_LEVELS.findIndex((l) => l.key === v);
    return idx >= 0 ? idx : 0;
  } catch { return 0; }
};

const applyTextSize = (idx) => {
  const lvl = TEXT_SIZE_LEVELS[idx];
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.style.fontSize = lvl.size;
  }
};

export const SettingsSheet = ({ open, onOpenChange }) => {
  const { languages, selectedLanguage, updateLanguage } = useZakatGlobal();
  const { lite, pref: litePref, setPref: setLitePref, autoDetected } = useLiteMode();
  const [textIdx, setTextIdx] = useState(readTextSize);

  useEffect(() => { applyTextSize(textIdx); }, [textIdx]);

  const pickTextSize = (idx) => {
    setTextIdx(idx);
    try { localStorage.setItem(TEXT_SIZE_KEY, TEXT_SIZE_LEVELS[idx].key); } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="settings-sheet"
        className="max-w-md w-[calc(100vw-2rem)] p-0 bg-white gap-0 rounded-2xl overflow-hidden"
      >
        <DialogHeader className="flex flex-row items-center justify-between gap-3 px-5 py-4 border-b border-stone-200 bg-gradient-to-r from-[#FAF8F5] to-[#F3EFEA]">
          <div>
            <DialogTitle className="font-heading text-[18px] text-[#0D1520]">Settings</DialogTitle>
            <DialogDescription className="text-[12px] text-stone-500">
              Language, region &amp; text size
            </DialogDescription>
          </div>
          <DialogClose
            data-testid="settings-close"
            aria-label="Close settings"
            className="inline-flex w-8 h-8 items-center justify-center rounded-full text-stone-500 hover:bg-stone-100 hover:text-[#0D1520] transition-colors"
          >
            <X className="w-4 h-4" />
          </DialogClose>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Country / region */}
          <section aria-labelledby="settings-country-label">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-[#0D4733]" />
              <h3 id="settings-country-label" className="text-[13px] font-semibold uppercase tracking-wider text-[#0D1520]">
                Country &amp; currency
              </h3>
            </div>
            <div data-testid="settings-country-slot" className="[&_button]:w-full">
              <CountryPicker variant="desktop" testid="settings-country-picker" />
            </div>
            <p className="mt-1.5 text-[11px] text-stone-500">
              Country sets your currency and country-specific Islamic legal guidance.
            </p>
          </section>

          {/* Language */}
          <section aria-labelledby="settings-language-label">
            <div className="flex items-center gap-2 mb-2">
              <Languages className="w-4 h-4 text-[#0D4733]" />
              <h3 id="settings-language-label" className="text-[13px] font-semibold uppercase tracking-wider text-[#0D1520]">
                Language
              </h3>
            </div>
            <div
              role="listbox"
              aria-label="Language"
              data-testid="settings-language-list"
              className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto rounded-lg border border-stone-200 p-1.5"
            >
              {languages.map((lang) => {
                const active = lang.code === selectedLanguage.code;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-testid={`settings-lang-${lang.code}`}
                    onClick={() => updateLanguage(lang.code)}
                    className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-start text-[13px] transition-colors ${
                      active
                        ? 'bg-[#EDF5F1] text-[#0D1520]'
                        : 'text-stone-700 hover:bg-[#F3EFEA]'
                    }`}
                  >
                    <span className="flex items-baseline gap-1.5 min-w-0">
                      <span className="font-mono text-[10px] uppercase tracking-wider opacity-70 w-6 shrink-0">
                        {lang.code}
                      </span>
                      <span className="truncate">{lang.nameNative}</span>
                    </span>
                    {active && <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Text size */}
          <section aria-labelledby="settings-textsize-label">
            <div className="flex items-center gap-2 mb-2">
              <Type className="w-4 h-4 text-[#0D4733]" />
              <h3 id="settings-textsize-label" className="text-[13px] font-semibold uppercase tracking-wider text-[#0D1520]">
                Text size
              </h3>
            </div>
            <div
              role="radiogroup"
              aria-label="Text size"
              data-testid="settings-textsize-group"
              className="grid grid-cols-3 gap-1.5"
            >
              {TEXT_SIZE_LEVELS.map((lvl, i) => {
                const active = i === textIdx;
                return (
                  <button
                    key={lvl.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`settings-textsize-${lvl.key}`}
                    onClick={() => pickTextSize(i)}
                    className={`flex flex-col items-center justify-center py-2.5 rounded-lg border transition-colors ${
                      active
                        ? 'bg-[#EDF5F1] border-[#CBD2D9] text-[#0D1520]'
                        : 'bg-white border-stone-200 text-stone-700 hover:border-[#0D4733] hover:bg-[#F3EFEA]'
                    }`}
                  >
                    <span
                      className="font-heading font-bold"
                      style={{ fontSize: i === 0 ? 14 : i === 1 ? 18 : 22 }}
                    >
                      Aa
                    </span>
                    <span className="mt-0.5 text-[11px] font-medium">{lvl.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-stone-500">
              Text size preference is saved on this device.
            </p>
          </section>

          {/* Lite mode */}
          <section aria-labelledby="settings-lite-label">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-[#0D4733]" />
              <h3 id="settings-lite-label" className="text-[13px] font-semibold uppercase tracking-wider text-[#0D1520]">
                Lite mode
              </h3>
              {lite && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                  Active
                </span>
              )}
            </div>
            <div
              role="radiogroup"
              aria-label="Lite mode"
              data-testid="settings-lite-group"
              className="grid grid-cols-3 gap-1.5"
            >
              {[
                { key: 'auto', label: 'Auto' },
                { key: 'on',   label: 'On' },
                { key: 'off',  label: 'Off' },
              ].map((opt) => {
                const active = opt.key === litePref;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`settings-lite-${opt.key}`}
                    onClick={() => setLitePref(opt.key)}
                    className={`py-2 rounded-lg border text-[12.5px] font-medium transition-colors ${
                      active
                        ? 'bg-[#EDF5F1] border-[#CBD2D9] text-[#0D1520]'
                        : 'bg-white border-stone-200 text-stone-700 hover:border-[#0D4733] hover:bg-[#F3EFEA]'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-stone-500">
              Strips animations and videos so calculators work on slow networks.
              {' '}<span className="text-stone-400">
                Auto = on when your connection is slow{autoDetected ? ' (currently slow)' : ''}.
              </span>
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsSheet;
